<template>
  <div class="page max-w-[1010px] flex flex-col gap-6 mx-6 lg:mx-0">
    <div class="flex justify-between flex-col lg:flex-row w-full gap-4">
      <h1 class="!text-4xl font-bold">Featured Causes</h1>
      <Button
        class="text-black"
        variant="outlined"
        @click.prevent="showAddModal = true"
        data-test="add-featured-cause-button"
        ><i class="far fa-circle-plus mr-1 text-primary"></i>Add Featured Cause</Button
      >
    </div>
    <div class="flex justify-between w-full">
      <div class="flex grow relative max-w-[288px]">
        <InputText
          v-model="search"
          fluid
          placeholder="Search Organizations"
          class="pr-8"
          data-test="search-organizations-input"
        />
        <i class="fas fa-search absolute top-1/2 transform -translate-y-1/2 right-3 text-gray-400"></i>
      </div>
    </div>
    <div
      v-if="fundraiserStore.isLoading"
      class="flex gap-4"
    >
      <Skeleton
        width="288px"
        height="364px"
      />
    </div>
    <div
      v-else-if="0 == organizations.length"
      class="flex flex-grow flex-col text-center gap-4 justify-center"
    >
      <div class="text-xl font-bold">Highlight the organizations you care about</div>
      <div>Add organizations to your featured causes to highlight them on your company giving page.</div>
      <div>
        <Button
          variant="primary"
          size="large"
          class="px-8"
          @click.prevent="showAddModal = true"
          ><i class="far fa-circle-plus mr-1 text-white"></i>Add Featured Cause</Button
        >
      </div>
    </div>
    <div
      v-else
      class="flex gap-4 flex-wrap w-full"
      data-test="featured-causes-container"
    >
      <div
        v-for="(organization, index) in organizations"
        :key="index"
        class="flex flex-col items-center gap-4 bg-white shadow-md rounded-lg w-[288px] h-[364px] group"
        :data-test="'featured-cause-card-' + organization.organizations_id"
      >
        <div
          class="w-full h-[162px] rounded-t-lg bg-gray-200 relative overflow-hidden hover:shadow-[inset_0px_0px_40px_rgba(0,0,0,0.33)] transition-all duration-400"
        >
          <img
            :src="fundraiserStore.orgLogoUrl(organization.organizations_id)"
            alt="Organization Logo"
            class="w-full h-[162px] rounded-t-lg object-cover group-hover:scale-110 transition-all duration-200"
          />
          <Button
            class="!absolute top-0 right-0 z-50 hover:bg-primary/25 lg:opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200"
            variant="text"
            aria-haspopup="true"
            :aria-controls="`action_menu-${index}`"
            :data-test="'action-menu-button-' + organization.organizations_id"
            @click="toggleMenu(index, $event)"
          >
            <i class="text-xl fa fa-ellipsis text-black drop-shadow-[5px_0px_0px_0px_rgba(255,255,255,0.8)]"></i>
          </Button>
          <Menu
            :ref="(el) => (menus[index] = el)"
            :id="`action_menu-${index}`"
            :data-test="'action-menu-' + organization.organizations_id"
            :model="[
              {
                label: 'View Page',
                icon: 'fas fa-eye w-5 text-center',
                command: () => viewPage(organization),
              },
              {
                label: 'Remove',
                icon: 'fa fa-trash w-5 text-center',
                command: () => remove(organization),
              },
            ]"
            :popup="true"
          />
        </div>
        <div class="flex flex-col justify-center px-4 h-14">
          <div class="text-gray-700 font-semibold flex text-xl line-clamp-2">
            {{ organization.organizationName }}
          </div>
        </div>
        <div class="flex justify-center px-4">
          <Tag
            rounded
            severity="secondary"
            class="flex w-auto"
            :class="{ 'opacity-0': !organization.category }"
            >{{ organization.category ?? '&nbsp;' }}</Tag
          >
        </div>
        <div class="flex flex-col w-full px-4 gap-2 text-sm h-14 justify-center">
          <p>
            Budget:
            <span class="font-semibold">{{
              new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                trailingZeroDisplay: 'stripIfInteger',
              }).format(organization.operatingBudget)
            }}</span>
          </p>
          <p
            v-if="organization.county"
            class="text-ellipsis whitespace-nowrap overflow-hidden"
          >
            County: <span class="font-semibold">{{ organization.county }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
  <Modal
    :show="showAddModal"
    width="750px"
    height="530px"
    @close="showAddModal = false"
  >
    <template #header> Add Featured Cause </template>
    <LabeledField label="Organizations">
      <FeaturedCausesDropdown
        selectedContainerClass="mb-4 overflow-hidden overflow-y-scroll max-h-[220px]"
        :hidden="companyAdminStore.company?.featuredOrganizations"
        @change="(organizations) => (organizationsToAdd = organizations)"
      />
    </LabeledField>
    <template #footer>
      <Button
        variant="outlined"
        @click.prevent="showAddModal = false"
        data-test="cancel-button"
        >Cancel</Button
      >
      <Button
        variant="primary"
        @click.prevent="add()"
        data-test="add-button"
        :disabled="!organizationsToAdd.length"
      >
        Add<Badge
          severity="success"
          v-show="organizationsToAdd.length"
          data-test="add-badge"
          >{{ organizationsToAdd.length }}</Badge
        >
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useDonorStore } from '@/stores/DonorStore';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import Modal from '@/components/Modal.vue';
import LabeledField from '@/components/forms/LabeledField.vue';
import FeaturedCausesDropdown from '@/components/companyGiving/FeaturedCausesDropdown.vue';

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const menus = ref([]);
const organizationsToAdd = ref([]);
const search = ref('');
const showAddModal = ref(false);

const toggleMenu = (index, event) => {
  menus.value[index]?.toggle(event);
};

const viewPage = (organization) => {
  if (!organization.urlLink) {
    console.error('Organization URL link is undefined:', organization);
    return;
  }
  window.location = donorStore.hostUrl + '/organization/' + organization.urlLink;
};

const add = () => {
  if (!Array.isArray(companyAdminStore.company.featuredOrganizations)) {
    companyAdminStore.company.featuredOrganizations = [];
  }
  companyAdminStore.company.featuredOrganizations = [
    ...companyAdminStore.company.featuredOrganizations,
    ...organizationsToAdd.value,
  ];

  showAddModal.value = false;
  save();
};

const remove = (organization) => {
  if (!companyAdminStore.company) {
    console.error('Company data is not available.');
    return;
  }
  companyAdminStore.company.featuredOrganizations = companyAdminStore.company.featuredOrganizations.filter(
    (orgId) => orgId !== organization.organizations_id,
  );
  save();
};

const save = () => {
  companyAdminStore.patch({ featuredOrganizations: companyAdminStore.company.featuredOrganizations });
};

const organizations = computed(
  () =>
    // Sort by name alphabetically
    fundraiserStore.organizations
      ?.sort((a, b) => a.organizationName.localeCompare(b.organizationName))
      ?.filter(
        (org) =>
          companyAdminStore.company?.featuredOrganizations?.includes(org.organizations_id) &&
          org.organizationName.toLowerCase().includes(search.value.toLowerCase()),
      ) ?? [],
);

onBeforeMount(async () => {
  await useFundraiserStore().bringAllOrganizations();
});
</script>
